// src/components/sections/RecentlyViewedSection.jsx
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ListProduct } from "..";
import { fetchRecentProducts } from "../../redux/slices/recentViewsSlice";

import ProductCarousel from "./ProductCarousel";

/**
 * The "Recently viewed" rail — the reference's "Order again"-styled carousel
 * (`ProductCarousel`) over `state.recentViews` (localStorage-backed browsing
 * history). Renders nothing for a visitor with no history.
 *
 * Deliberately knows nothing about best sellers: `BestSellersSection` is a
 * standalone sibling. The landing composes the two (this rail first, best sellers
 * under it, so a first-time visitor still gets a full slot from the other one);
 * the PDP mounts this rail alone — a storewide best-sellers rail under a product
 * crowds the product (removed on request).
 */
export default function RecentlyViewedSection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { recentProducts, loading, error, viewedProductIds } = useSelector(
    (state) => state.recentViews
  );

  const hasHistory = viewedProductIds.length > 0;

  useEffect(() => {
    if (hasHistory && recentProducts.length === 0) {
      dispatch(fetchRecentProducts(viewedProductIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasRecents = recentProducts && recentProducts.length > 0;
  if (!hasRecents && !loading && !hasHistory) {
    return null;
  }

  return (
    <ProductCarousel
      eyebrow={
        hasRecents
          ? `Continue where you left off · ${recentProducts.length} ${
              recentProducts.length === 1 ? "product" : "products"
            }`
          : "Continue where you left off"
      }
      title="Recently viewed"
      items={recentProducts}
      loading={loading}
      error={error}
      viewAllText="Browse the catalogue"
      onViewAll={() => navigate("/products")}
      renderItem={(item) => <ListProduct product={item} listType="grid" />}
    />
  );
}
